import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { PdfExportSettings, SynthesisGroup } from '../api/types';
import { CompiledPdfExportSettingsModal } from './CompiledPdfExportSettingsModal';
import { COMPILED_DEFAULT_SETTINGS, generateCompiledSynthesisPdf } from '../utils/compiledPdfExport';
import { useAuth } from '../contexts/AuthContext';

/**
 * Export 📄/réglages 🖨️ du diagramme graphique COMPILÉ (fusion de tous les
 * groupes) — réutilisé à l'identique par SynthesisPage et WorkloadPage,
 * affiché uniquement en vue Diagramme (rien à exporter du compilé en vue
 * Tableau, qui montre les tableaux PAR diagramme source — voir
 * SourceDiagramExportCard pour ceux-là). `title` sert à la fois d'en-tête
 * dans le PDF et de nom de fichier — voir SynthesisPage/WorkloadPage pour
 * sa construction (reprend l'intitulé déjà affiché à l'écran).
 */
export function CompiledExportToolbar({ groups, title }: { groups: SynthesisGroup[]; title: string }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runExport = async (settings: Required<PdfExportSettings>) => {
    setError(null);
    setExporting(true);
    try {
      await generateCompiledSynthesisPdf({
        groups,
        title,
        settings,
        dateFormat: user?.dateFormat ?? 'DD-MM-YYYY',
        weekNumbering: user?.weekNumbering ?? 'ISO',
        locale: i18n.language,
        weekAbbrev: (n: number) => t('chart.weekAbbrev', { n }),
        columnLabel: (col: string) => t(`tasks.colShort.${col}`),
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="toolbar" style={{ marginBottom: 8, alignItems: 'center' }}>
      <button
        className="btn icon-btn"
        onClick={() => runExport(COMPILED_DEFAULT_SETTINGS)}
        disabled={exporting}
        title={t(exporting ? 'diagram.exporting' : 'diagram.exportPdf') as string}
      >
        📄
      </button>
      <button className="btn icon-btn" onClick={() => setShowSettings(true)} title={t('pdfExport.title') as string}>
        🖨️
      </button>
      {error && <span className="error-text">{error}</span>}
      {showSettings && (
        <CompiledPdfExportSettingsModal groups={groups} onClose={() => setShowSettings(false)} onConfirm={(settings) => runExport(settings)} />
      )}
    </div>
  );
}
